import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'

import { THEME_COLOR_STORAGE_KEY } from '../constants/theme.constant'

export interface ThemeColorOption {
	key: string
	label: string
	color: string
	primary: string
	secondary: string
	tertiary: string
}

@Injectable({
	providedIn: 'root',
})
export class ThemeColorService {
	private readonly storageKey = THEME_COLOR_STORAGE_KEY

	private readonly customThemeClass = 'has-custom-theme-color'

	private readonly themeClassPrefix = 'theme-color-'

	readonly colorOptions: ThemeColorOption[] = [
		{ key: 'navy-blue', label: 'Navy Blue', color: '#020d31', primary: '#f5b430', secondary: '#020d31', tertiary: '#5d6f7a' },
		{ key: 'modern-olive', label: 'Modern Olive', color: '#1c280d', primary: '#f5b430', secondary: '#1c280d', tertiary: '#5d6f7a' },
		{ key: 'dark-gray', label: 'Dark Gray', color: '#12191a', primary: '#f5b430', secondary: '#12191a', tertiary: '#5d6f7a' },
		{ key: 'warm-ember', label: 'Warm Ember', color: '#230501', primary: '#f5b430', secondary: '#230501', tertiary: '#5d6f7a' },
		{ key: 'dark-green', label: 'Dark Green', color: '#1B4332', primary: '#5AC481FF', secondary: '#1B4332', tertiary: '#ffffff' },
		{ key: 'silver-grey', label: 'Silver Grey', color: '#5e6d78', primary: '#e7eff5', secondary: '#5e6d78', tertiary: '#F5F6F7' },
		{ key: 'dark-sky-blue', label: 'Dark Sky Blue', color: '#19639A', primary: '#f5b430', secondary: '#19639A', tertiary: '#F4F7FC' },
		{ key: 'chocolate-brown', label: 'Chocolate Brown', color: '#3f2515', primary: '#E8D5B7', secondary: '#3f2515', tertiary: '#FAF6F0' },
		{ key: 'charcoal-grey', label: 'Charcoal Grey', color: '#2E3440', primary: '#9AA3B0', secondary: '#2E3440', tertiary: '#F5F6F7' },
		{ key: 'deep-space', label: 'Deep Space', color: '#16162A', primary: '#E8A050', secondary: '#16162A', tertiary: '#FFF8F0' },
		{ key: 'deep-rose', label: 'Deep Rose', color: '#c4606e', primary: '#ecdfe1', secondary: '#c4606e', tertiary: '#e4c8cc' },
		{ key: 'light-gold', label: 'Light Gold', color: '#f4b840', primary: '#f4b840', secondary: '#424242', tertiary: '#f7d78a' },
	]

	readonly defaultColor = 'navy-blue'

	constructor(@Inject(DOCUMENT) private readonly document: Document) {
		const storedThemeColor = this.getStoredThemeColor()
		this.setCustomThemeClass(Boolean(storedThemeColor))
		this.setThemeColorClass(storedThemeColor)
		this.applyThemeColor(storedThemeColor ?? this.defaultColor)
	}

	getThemeColor(): string {
		return this.getStoredThemeColor() ?? this.defaultColor
	}

	setThemeColor(colorKey: string): void {
		const option = this.colorOptions.find((opt) => opt.key === colorKey)
		if (!option) {
			console.warn(`Invalid theme color: "${colorKey}". Falling back to default.`)
			colorKey = this.defaultColor
		}

		localStorage.setItem(this.storageKey, colorKey)
		this.setCustomThemeClass(true)
		this.setThemeColorClass(colorKey)
		this.applyThemeColor(colorKey)
	}

	private applyThemeColor(colorKey: string): void {
		const option = this.colorOptions.find((opt) => opt.key === colorKey)
		if (!option) return

		const existingStyle = this.document.getElementById('custom-theme-color')
		if (existingStyle) {
			existingStyle.remove()
		}

		const style = this.document.createElement('style')
		style.id = 'custom-theme-color'

		const generateVars = (name: string, hex: string) => {
			const rgb = this.hexToRgb(hex)
			const shade = this.adjustColor(hex, -10)
			const tint = this.adjustColor(hex, 10)
			const contrast = this.getContrastColor(hex)
			const contrastRgb = this.hexToRgb(contrast)

			return `
				--ion-color-${name}: ${hex} !important;
				--ion-color-${name}-rgb: ${rgb} !important;
				--ion-color-${name}-contrast: ${contrast} !important;
				--ion-color-${name}-contrast-rgb: ${contrastRgb} !important;
				--ion-color-${name}-shade: ${shade} !important;
				--ion-color-${name}-tint: ${tint} !important;
			`
		}

		style.textContent = `
			* {
				${generateVars('primary', option.primary)}
				${generateVars('secondary', option.secondary)}
				${generateVars('tertiary', option.tertiary)}

				--ion-color-header: ${option.secondary} !important;
				--ion-color-header-rgb: ${this.hexToRgb(option.secondary)} !important;
				--ion-color-header-contrast: ${this.getContrastColor(option.secondary)} !important;
				--ion-color-header-contrast-rgb: ${this.hexToRgb(this.getContrastColor(option.secondary))} !important;
				--ion-color-header-shade: ${this.adjustColor(option.secondary, -10)} !important;
				--ion-color-header-tint: ${this.adjustColor(option.secondary, 10)} !important;

				--ion-color-drawer: ${option.secondary} !important;
				--ion-color-drawer-rgb: ${this.hexToRgb(option.secondary)} !important;
				--ion-color-drawer-contrast: ${this.getContrastColor(option.secondary)} !important;
				--ion-color-drawer-contrast-rgb: ${this.hexToRgb(this.getContrastColor(option.secondary))} !important;
				--ion-color-drawer-shade: ${this.adjustColor(option.secondary, -10)} !important;
				--ion-color-drawer-tint: ${this.adjustColor(option.secondary, 10)} !important;
			}
		`
		this.document.head.appendChild(style)
	}

	private getStoredThemeColor(): string | null {
		const color = localStorage.getItem(this.storageKey)
		return this.colorOptions.some((opt) => opt.key === color) ? color : null
	}

	private setCustomThemeClass(hasCustomTheme: boolean): void {
		this.document.body.classList.toggle(this.customThemeClass, hasCustomTheme)
	}

	private setThemeColorClass(colorKey: string | null): void {
		for (const option of this.colorOptions) {
			this.document.body.classList.remove(`${this.themeClassPrefix}${option.key}`)
		}

		if (colorKey) this.document.body.classList.add(`${this.themeClassPrefix}${colorKey}`)
	}

	private hexToRgb(hex: string): string {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
		return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0'
	}

	private adjustColor(hex: string, percent: number): string {
		const num = parseInt(hex.replace('#', ''), 16)
		const amt = Math.round(2.55 * percent)
		const R = (num >> 16) + amt
		const G = ((num >> 8) & 0x00ff) + amt
		const B = (num & 0x0000ff) + amt
		return (
			'#' +
			(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255))
				.toString(16)
				.slice(1)
		)
	}

	private getContrastColor(hex: string): string {
		const rgbStr = this.hexToRgb(hex)
		const [r, g, b] = rgbStr.split(',').map((x) => parseInt(x.trim()))
		const brightness = (r * 299 + g * 587 + b * 114) / 1000
		return brightness > 128 ? '#000000' : '#ffffff'
	}
}
